import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, KeyRound, Pencil, Plus, Trash2, UserCheck, UserX } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card, CardBody } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Avatar } from "@/ui/Avatar";
import { Button } from "@/ui/Button";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { LoadingState, EmptyState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatDZD } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import type { RoleName } from "@/lib/permissions";
import type { CompensationType, Currency, TeamMember, TeamRole, TeamStatus } from "@/types";
import {
  useArchiveTeamMember,
  useCreateTeamMember,
  useTeamMembers,
  useUpdateTeamMember,
} from "./team.hooks";
import type { ApiTeamMember, TeamMemberInput } from "./team.adapter";

const AUTH_ROLES: RoleName[] = ["OWNER", "ADMIN", "MANAGER", "MEDIA_BUYER", "CREATIVE", "ACCOUNTANT", "VIEWER"];
const COMPENSATION_TYPES: CompensationType[] = ["FIXED", "FIXED_BONUS", "PERCENTAGE", "PER_ORDER", "FREELANCE"];
const CURRENCIES: Currency[] = ["DZD", "USD", "EUR"];
const STATUSES: TeamStatus[] = ["ACTIVE", "ON_LEAVE", "INACTIVE"];

function defaultAccountRole(role: TeamRole): RoleName {
  if (role === "OWNER") return "OWNER";
  if (role === "MEDIA_BUYER") return "MEDIA_BUYER";
  if (role === "CREATIVE") return "CREATIVE";
  if (role === "ACCOUNT_MANAGER" || role === "MARKETING_STRATEGIST") return "MANAGER";
  return "VIEWER";
}

function accountBadge(member: TeamMember, t: (key: string) => string) {
  if (!member.account) {
    return (
      <Badge tone="default">
        <UserX className="h-3 w-3" /> {t("team.noLinkedAccount")}
      </Badge>
    );
  }
  return (
    <Badge tone={member.account.isActive ? "success" : "warning"} dot>
      {member.account.isActive ? t("team.accountActive") : t("team.accountInactive")}
    </Badge>
  );
}

export function TeamPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { TEAM_ROLE } = useLabels();
  const { members, isLoading, isError, refetch } = useTeamMembers();
  const createMember = useCreateTeamMember();
  const updateMember = useUpdateTeamMember();
  const archiveMember = useArchiveTeamMember();
  const [toDelete, setToDelete] = useState<TeamMember | undefined>();
  const [editing, setEditing] = useState<TeamMember | undefined>();
  const [addOpen, setAddOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<{ email: string; password: string } | undefined>();

  const counts = useMemo(() => {
    const by = (role: TeamRole) => members.filter((m) => m.role === role).length;
    return {
      owners: by("OWNER"),
      buyers: by("MEDIA_BUYER"),
      creatives: by("CREATIVE"),
      managers: by("ACCOUNT_MANAGER"),
    };
  }, [members]);

  const onCreate = async (input: TeamMemberInput) => {
    try {
      const result = (await createMember.mutateAsync(input)) as ApiTeamMember;
      toast.success(result.accountCreated ? t("team.accountCreated") : t("team.added"));
      if (result.temporaryPassword) {
        setTemporaryPassword({ email: result.linkedAccount?.email ?? input.accountEmail ?? input.email, password: result.temporaryPassword });
      }
      setAddOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t("team.addFailed")));
    }
  };

  const onUpdate = async (input: TeamMemberInput) => {
    if (!editing) return;
    try {
      const result = (await updateMember.mutateAsync({ id: editing.id, input })) as ApiTeamMember;
      toast.success(result.accountCreated ? t("team.accountCreated") : t("team.updated"));
      if (result.temporaryPassword) {
        setTemporaryPassword({ email: result.linkedAccount?.email ?? input.accountEmail ?? input.email, password: result.temporaryPassword });
      }
      setEditing(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err, t("team.updateFailed")));
    }
  };

  const onArchiveConfirm = async () => {
    if (!toDelete) return;
    try {
      await archiveMember.mutateAsync(toDelete.id);
      toast.success(t("team.archived"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("team.archiveFailed")));
    } finally {
      setToDelete(undefined);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("team.title")}
        subtitle={t("team.subtitle", { count: members.length, active: members.filter((m) => m.status === "ACTIVE").length })}
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-3 w-3" /> {t("team.add")}
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPICard label={t("team.kpis.owners")} value={counts.owners} />
        <KPICard label={t("team.kpis.mediaBuyers")} value={counts.buyers} />
        <KPICard label={t("team.kpis.creatives")} value={counts.creatives} />
        <KPICard label={t("team.kpis.accountManagers")} value={counts.managers} />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      {isLoading ? (
        <Card><LoadingState rows={4} /></Card>
      ) : members.length === 0 ? (
        <Card><EmptyState title={t("team.emptyTitle")} message={t("team.emptyMessage")} /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
          {members.map((member) => (
            <Card key={member.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 mb-3.5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar name={member.name} size={38} />
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold text-text truncate">{member.name}</div>
                      <div className="text-[11px] text-text3 truncate">{member.email}</div>
                    </div>
                  </div>
                  <Badge tone={member.status === "ACTIVE" ? "success" : member.status === "ON_LEAVE" ? "warning" : "danger"} dot>
                    {member.status === "ACTIVE" ? t("team.status.active") : member.status === "ON_LEAVE" ? t("team.status.onLeave") : t("team.status.inactive")}
                  </Badge>
                </div>

                <div className="flex gap-1.5 flex-wrap mb-3">
                  <Badge tone={TEAM_ROLE[member.role].tone}>{TEAM_ROLE[member.role].label}</Badge>
                  {accountBadge(member, t)}
                </div>

                <div className="pt-2.5 border-t border-border space-y-1.5">
                  <div className="flex justify-between gap-3 text-[11px]">
                    <span className="text-text2">{t("team.salaryBase")}</span>
                    <span className="mono text-text">{formatDZD(member.salary)}</span>
                  </div>
                  <div className="flex justify-between gap-3 text-[11px]">
                    <span className="text-text2">{t("team.assignedClients")}</span>
                    <span className="text-text">{member.clientsCount || "-"}</span>
                  </div>
                  <div className="flex justify-between gap-3 text-[11px]">
                    <span className="text-text2">{t("team.linkedAccount")}</span>
                    <span className="text-text text-right truncate">{member.account?.email ?? t("team.noLinkedAccount")}</span>
                  </div>
                  {member.account?.roles[0] && (
                    <div className="flex justify-between gap-3 text-[11px]">
                      <span className="text-text2">{t("team.permissions")}</span>
                      <span className="text-text text-right">{member.account.roles[0].label || member.account.roles[0].name}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-1.5 mt-3">
                  <Button variant="ghost" size="xs" onClick={() => navigate(`/team/${member.id}`)}>
                    <Eye className="h-3 w-3" /> {t("team.profile")}
                  </Button>
                  <Button variant="ghost" size="xs" onClick={() => setEditing(member)}>
                    <Pencil className="h-3 w-3" /> {t("common.edit")}
                  </Button>
                  <Button variant="ghost" size="xs" className="text-danger ml-auto" onClick={() => setToDelete(member)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <MemberFormModal open={addOpen} onClose={() => setAddOpen(false)} onSave={onCreate} />
      <MemberFormModal open={!!editing} onClose={() => setEditing(undefined)} onSave={onUpdate} initial={editing} />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(undefined)}
        title={t("team.archiveTitle")}
        message={t("team.archiveMessage", { name: toDelete?.name })}
        confirmLabel={t("common.archive")}
        onConfirm={onArchiveConfirm}
      />

      <Modal
        open={!!temporaryPassword}
        onClose={() => setTemporaryPassword(undefined)}
        title={t("team.temporaryPassword")}
        footer={<Button size="sm" onClick={() => setTemporaryPassword(undefined)}>{t("common.save")}</Button>}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[12.5px] text-text">
            <KeyRound className="h-4 w-4 text-primary" />
            <span>{temporaryPassword?.email}</span>
          </div>
          <div className="rounded-sm border border-border bg-bg3 px-3 py-2 mono text-[13px] text-text">
            {temporaryPassword?.password}
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

function MemberFormModal({
  open,
  onClose,
  onSave,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (input: TeamMemberInput) => void;
  initial?: TeamMember;
}) {
  const { t } = useTranslation();
  const { TEAM_ROLE } = useLabels();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<TeamRole>("MEDIA_BUYER");
  const [compensationType, setCompensationType] = useState<CompensationType>("FIXED");
  const [salary, setSalary] = useState("");
  const [currency, setCurrency] = useState<Currency>("DZD");
  const [status, setStatus] = useState<TeamStatus>("ACTIVE");
  const [createLoginAccount, setCreateLoginAccount] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [accountRole, setAccountRole] = useState<RoleName>("MEDIA_BUYER");
  const [accountIsActive, setAccountIsActive] = useState(true);
  const [error, setError] = useState<{ name?: boolean; email?: boolean; password?: boolean; message?: string }>({});

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setEmail(initial?.email ?? "");
    setPhone(initial?.phone ?? "");
    setRole(initial?.role ?? "MEDIA_BUYER");
    setCompensationType(initial?.compensation ?? "FIXED");
    setSalary(initial?.salary ? String(initial.salary) : "");
    setCurrency(initial?.currency ?? "DZD");
    setStatus(initial?.status ?? "ACTIVE");
    setCreateLoginAccount(false);
    setAccountEmail(initial?.account?.email ?? initial?.email ?? "");
    setTemporaryPassword("");
    setAccountRole(initial?.account?.roles[0]?.name ?? defaultAccountRole(initial?.role ?? "MEDIA_BUYER"));
    setAccountIsActive(initial?.account?.isActive ?? true);
    setError({});
  }, [initial, open]);

  const hasAccount = Boolean(initial?.account);

  const submit = () => {
    const nameErr = !name.trim();
    const emailErr = !email.trim();
    const passwordErr = !hasAccount && createLoginAccount && temporaryPassword.length < 8;
    if (nameErr || emailErr || passwordErr) {
      setError({
        name: nameErr,
        email: emailErr,
        password: passwordErr,
        message: passwordErr ? t("team.passwordRequired") : t("team.required"),
      });
      return;
    }
    onSave({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      role,
      compensationType,
      salary: Number(salary) || 0,
      currency,
      status,
      createLoginAccount: hasAccount ? false : createLoginAccount,
      accountEmail: accountEmail.trim() || email.trim(),
      temporaryPassword: temporaryPassword.trim() || undefined,
      accountRole,
      accountIsActive,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? t("team.editTitle") : t("team.addTitle")}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>{t("common.cancel")}</Button>
          <Button size="sm" onClick={submit}>{initial ? t("common.save") : t("common.add")}</Button>
        </>
      }
      maxWidth={680}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("team.name")} required error={error.name ? error.message : undefined}>
          <input className={fieldInputClass} value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label={t("team.email")} required error={error.email && !error.name ? error.message : undefined}>
          <input className={fieldInputClass} value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
      </div>

      <Field label={t("team.phone")}>
        <input className={fieldInputClass} value={phone} onChange={(event) => setPhone(event.target.value)} />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t("team.role")} required>
          <select
            className={fieldInputClass}
            value={role}
            onChange={(event) => {
              const nextRole = event.target.value as TeamRole;
              setRole(nextRole);
              if (!hasAccount) setAccountRole(defaultAccountRole(nextRole));
            }}
          >
            {(Object.keys(TEAM_ROLE) as TeamRole[]).map((option) => <option key={option} value={option}>{TEAM_ROLE[option].label}</option>)}
          </select>
        </Field>
        <Field label={t("team.statusLabel")} required>
          <select className={fieldInputClass} value={status} onChange={(event) => setStatus(event.target.value as TeamStatus)}>
            {STATUSES.map((option) => <option key={option} value={option}>{t(`team.status.${option === "ACTIVE" ? "active" : option === "ON_LEAVE" ? "onLeave" : "inactive"}`)}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={t("team.compensationType")}>
          <select className={fieldInputClass} value={compensationType} onChange={(event) => setCompensationType(event.target.value as CompensationType)}>
            {COMPENSATION_TYPES.map((option) => <option key={option} value={option}>{t(`team.compensation.${option}`)}</option>)}
          </select>
        </Field>
        <Field label={t("team.salary")}>
          <input type="number" className={fieldInputClass} value={salary} onChange={(event) => setSalary(event.target.value)} />
        </Field>
        <Field label={t("team.currency")}>
          <select className={fieldInputClass} value={currency} onChange={(event) => setCurrency(event.target.value as Currency)}>
            {CURRENCIES.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-1 border-t border-border pt-4">
        {hasAccount ? (
          <div className="mb-3 flex items-center gap-2 text-[12px] text-success">
            <UserCheck className="h-4 w-4" /> {t("team.linkedAccount")}
          </div>
        ) : (
          <label className="mb-3 flex items-center gap-2 text-[12.5px] font-semibold text-text">
            <input
              type="checkbox"
              checked={createLoginAccount}
              onChange={(event) => setCreateLoginAccount(event.target.checked)}
            />
            {t("team.createLoginAccount")}
          </label>
        )}

        {(hasAccount || createLoginAccount) && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("team.accountEmail")}>
                <input className={fieldInputClass} value={accountEmail} onChange={(event) => setAccountEmail(event.target.value)} />
              </Field>
              <Field label={t("team.accountRole")}>
                <select className={fieldInputClass} value={accountRole} onChange={(event) => setAccountRole(event.target.value as RoleName)}>
                  {AUTH_ROLES.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
            </div>
            <Field label={t("team.temporaryPassword")} required={!hasAccount} error={error.password ? error.message : undefined}>
              <input
                type="password"
                className={fieldInputClass}
                value={temporaryPassword}
                onChange={(event) => setTemporaryPassword(event.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-[12.5px] font-semibold text-text">
              <input
                type="checkbox"
                checked={accountIsActive}
                onChange={(event) => setAccountIsActive(event.target.checked)}
              />
              {accountIsActive ? t("team.accountActive") : t("team.accountInactive")}
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}
